import type { SharedLinkBill, SharedLinkPayload } from '@storeData/sharedLink';
import type { SharedLinkKind } from '@util/sharedLinkRoutes';
import React, { createContext, useContext, useMemo } from 'react';

const SharedLinkDataContext = createContext<SharedLinkPayload | null>(null);

export const SharedLinkDataProvider = ({
    payload,
    children
}: {
    payload: SharedLinkPayload | null;
    children: React.ReactNode;
}) => (
    <SharedLinkDataContext.Provider value={payload || null}>
        {children}
    </SharedLinkDataContext.Provider>
);

/**
 * Returns the server-prefetched record only when it is the one this page is asking for. After a
 * client-side navigation to a different id the payload is stale, so the caller falls back to its
 * normal fetch.
 */
export const useSharedLinkInitialData = (kind: SharedLinkKind, id?: string) => {
    const payload = useContext(SharedLinkDataContext);

    return useMemo(() => {
        if (!payload || !id) return null;
        return payload.kind === kind && payload.id === id ? payload.data : null;
    }, [payload, kind, id]);
};

/**
 * The bill HTML rendered on the server for this exact order.
 */
export const useSharedLinkBill = (id?: string): SharedLinkBill | null => {
    const payload = useContext(SharedLinkDataContext);

    return useMemo(() => {
        if (!payload || !id) return null;
        return payload.id === id ? payload.bill || null : null;
    }, [payload, id]);
};
